import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Business,
  CheckCircle,
  Email,
  Favorite,
  FavoriteBorder,
  Instagram,
  Language,
  LocationOn,
  Phone,
  Share,
  WhatsApp,
} from '@mui/icons-material';
import { empresaService, Empresa } from '../services/empresaService';
import { maskCNPJ, maskPhoneBR } from '../utils/inputMasks';

const cleanPhone = (value?: string) => (value || '').replace(/\D/g, '');
const ramoLabels: Record<string, string> = {
  comercio: 'Comércio',
  industrial: 'Industrial',
  prestacao_servico: 'Prestação de Serviço',
};
const formatRamoAtuacao = (value?: string) => value ? ramoLabels[value] || value : undefined;
const formatPhone = (value?: string) => maskPhoneBR(value || '') || value || '';
const formatEnderecoCompleto = (empresa: Empresa) => [
  [empresa.endereco, empresa.numero].filter(Boolean).join(', '),
  empresa.complemento,
  empresa.bairro,
  [empresa.cidade, empresa.estado].filter(Boolean).join(' - '),
  empresa.cep ? `CEP ${empresa.cep}` : '',
].filter(Boolean).join(' · ');
const splitServices = (empresa: Empresa) => (empresa.servicos || empresa.descricao_servico || '')
  .split(/\n|;|,/)
  .map((item) => item.trim())
  .filter(Boolean);

const EmpresaDetalhes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    empresaService.obterEmpresa(Number(id))
      .then((response) => {
        if (response.success && response.data) setEmpresa(response.data);
        else setError('Empresa não encontrada');
      })
      .catch((err) => setError(err.response?.data?.message || 'Erro ao carregar empresa'))
      .finally(() => setLoading(false));
  }, [id]);

  const services = useMemo(() => empresa ? splitServices(empresa) : [], [empresa]);

  const handleCompartilhar = () => {
    if (!empresa) return;
    const title = empresa.nome_fantasia || empresa.razao_social;
    if (navigator.share) {
      navigator.share({ title, text: `Confira ${title} no IDECONECTA`, url: window.location.href });
      return;
    }
    navigator.clipboard.writeText(window.location.href);
  };

  const handleContato = () => {
    if (!empresa) return;
    const phone = cleanPhone(empresa.whatsapp || empresa.telefone || empresa.celular);
    if (!phone) return;
    const text = encodeURIComponent(`Olá, encontrei sua empresa no IDECONECTA e gostaria de mais informações.`);
    window.open(`https://wa.me/55${phone}?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress sx={{ color: '#C23535' }} /></Box>;
  }

  if (error || !empresa) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Empresa não encontrada'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/busca')} sx={{ color: '#C23535' }}>Voltar para busca</Button>
      </Container>
    );
  }

  const companyName = empresa.nome_fantasia || empresa.razao_social;
  const phone = cleanPhone(empresa.whatsapp || empresa.telefone || empresa.celular);

  return (
    <Box sx={{ bgcolor: '#f4f4f5', minHeight: '100vh' }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          py: { xs: 5, md: 7 },
          background: '#C23535',
          '&:before': {
            content: '""', position: 'absolute', inset: 0,
            backgroundImage: 'linear-gradient(90deg, rgba(255, 120, 120, 0.16) 1px, transparent 1px), linear-gradient(0deg, rgba(255, 120, 120, 0.16) 1px, transparent 1px)',
            backgroundSize: '34px 34px', opacity: 0.85,
          },
          '&:after': {
            content: '""', position: 'absolute', width: 360, height: 360, right: -120, top: -120,
            border: '48px solid rgba(255,255,255,0.08)', transform: 'rotate(22deg)', borderRadius: 4,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/busca')} sx={{ color: 'rgba(255,255,255,0.9)', mb: 3, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>Voltar para busca</Button>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box
              sx={{
                width: { xs: 92, md: 112 },
                height: { xs: 92, md: 112 },
                bgcolor: 'rgba(255,255,255,0.18)',
                border: '3px solid rgba(255,255,255,0.38)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
                overflow: 'hidden',
                flex: '0 0 auto',
              }}
            >
              {empresa.logo_url ? (
                <Box component="img" src={empresa.logo_url} alt={`Logo ${companyName}`} sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              ) : (
                <Business sx={{ fontSize: 42 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h3" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.05, wordBreak: 'break-word', color: '#fff', textShadow: '0 2px 14px rgba(0,0,0,0.28)' }}>{companyName}</Typography>
              {empresa.nome_fantasia && <Typography sx={{ color: 'rgba(255,255,255,0.82)', mt: 1 }}>{empresa.razao_social}</Typography>}
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                <Chip icon={<CheckCircle />} label="Empresa verificada" sx={{ bgcolor: 'rgba(46,125,50,0.86)', color: '#fff', fontWeight: 800, '& .MuiChip-icon': { color: '#fff' } }} />
                {empresa.cidade && <Chip icon={<LocationOn />} label={`${empresa.cidade}${empresa.estado ? `, ${empresa.estado}` : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />}
              </Stack>
            </Box>
            <Stack direction="row" gap={1}>
              <IconButton onClick={() => setFavorited((value) => !value)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' }}>{favorited ? <Favorite /> : <FavoriteBorder />}</IconButton>
              <IconButton onClick={handleCompartilhar} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' }}><Share /></IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 12px 32px rgba(32,37,41,0.08)', border: '1px solid rgba(32,37,41,0.08)', mb: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#C23535', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><Business /> Sobre a Empresa</Typography>
                <Typography sx={{ lineHeight: 1.85, color: '#2C2C2C', whiteSpace: 'pre-line' }}>{empresa.descricao_servico}</Typography>

                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#C23535', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle /> Serviços oferecidos</Typography>
                <Grid container spacing={1.5}>
                  {services.length > 0 ? services.map((service) => (
                    <Grid item xs={12} sm={6} key={service}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fff7f7', border: '1px solid rgba(194,53,53,0.14)', height: '100%' }}>
                        <Typography sx={{ fontWeight: 700, color: '#2C2C2C' }}>{service}</Typography>
                      </Box>
                    </Grid>
                  )) : (
                    <Grid item xs={12}><Alert severity="info">Esta empresa ainda não detalhou os serviços.</Alert></Grid>
                  )}
                </Grid>

                <Button fullWidth size="large" variant="contained" startIcon={<WhatsApp />} onClick={handleContato} disabled={!phone} sx={{ mt: 3, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, borderRadius: 2, fontWeight: 900, py: 1.4 }}>
                  Entrar em contato
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 12px 32px rgba(32,37,41,0.08)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#C23535', mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}><Phone /> Contato</Typography>
                  {empresa.whatsapp && <InfoLine icon={<WhatsApp />} label="WhatsApp" value={formatPhone(empresa.whatsapp)} />}
                  {empresa.telefone && <InfoLine icon={<Phone />} label="Telefone" value={formatPhone(empresa.telefone)} />}
                  {empresa.email_empresa && <InfoLine icon={<Email />} label="Email" value={empresa.email_empresa} />}
                  {empresa.website && <InfoLine icon={<Language />} label="Site" value={empresa.website} href={empresa.website} />}
                  {empresa.instagram && <InfoLine icon={<Instagram />} label="Instagram" value={`@${empresa.instagram.replace('@', '')}`} href={`https://instagram.com/${empresa.instagram.replace('@', '')}`} />}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, boxShadow: '0 12px 32px rgba(32,37,41,0.08)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#C23535', mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}><LocationOn /> Localização</Typography>
                  <Typography fontWeight={700}>{formatEnderecoCompleto(empresa) || 'Endereço não informado'}</Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, boxShadow: '0 12px 32px rgba(32,37,41,0.08)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#C23535', mb: 2 }}>Dados cadastrais</Typography>
                  <InfoValue label="CNPJ" value={maskCNPJ(empresa.cnpj)} />
                  <InfoValue label="Ramo de atuação" value={formatRamoAtuacao(empresa.ramo_atuacao)} />
                  <InfoValue label="Categoria" value={empresa.categoria_nome || empresa.categoria_personalizada} />
                  <InfoValue label="Responsável" value={empresa.nome} />
                  <InfoValue label="Membro desde" value={new Date(empresa.criada_em || Date.now()).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })} />
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const InfoLine: React.FC<{ icon: React.ReactNode; label: string; value: string; href?: string }> = ({ icon, label, value, href }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderBottom: '1px solid rgba(32,37,41,0.06)' }}>
    <Box sx={{ color: '#C23535', display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800 }}>{label}:</Typography>
      <Typography component={href ? 'a' : 'p'} href={href} target={href ? '_blank' : undefined} rel={href ? 'noopener noreferrer' : undefined} sx={{ m: 0, color: href ? '#C23535' : '#2C2C2C', fontWeight: 700, textDecoration: 'none', wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  </Box>
);

const InfoValue: React.FC<{ label: string; value?: string }> = ({ label, value }) => value ? (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800 }}>{label}</Typography>
    <Typography fontWeight={700} sx={{ wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
) : null;

export default EmpresaDetalhes;
